import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { saas, errMsg } from "@/lib/saasApi";

const TYPES = [
  { id: "manual_grant", label: "Grant credits (+)" },
  { id: "correction", label: "Correction (+/-)" },
  { id: "admin_refund", label: "Refund (+)" },
  { id: "monthly_grant", label: "Monthly grant (+)" },
];

export default function AdminCredits() {
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState("");
  const [ledger, setLedger] = useState([]);
  const [balance, setBalance] = useState(null);
  const [form, setForm] = useState({ amount: "", type: "manual_grant", reason: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { saas.adminOrgs().then(({ data }) => setOrgs(data)).catch((e) => toast.error(errMsg(e))); }, []);

  const loadLedger = useCallback((id) => {
    saas.adminLedger(id).then(({ data }) => setLedger(data)).catch((e) => toast.error(errMsg(e)));
    const o = orgs.find((x) => x.id === id);
    setBalance(o ? o.availableCredits : null);
  }, [orgs]);

  useEffect(() => { if (orgId) loadLedger(orgId); }, [orgId, loadLedger]);

  const submit = async () => {
    const amt = parseInt(form.amount, 10);
    if (!orgId) return toast.error("Choose a customer.");
    if (!Number.isInteger(amt) || amt === 0) return toast.error("Enter a whole number (not 0).");
    if (!form.reason.trim()) return toast.error("A reason is required.");
    setBusy(true);
    try {
      const { data } = await saas.adminGrant({ organisationId: orgId, amount: amt, type: form.type, reason: form.reason.trim() });
      setBalance(data.balance);
      setForm({ amount: "", type: form.type, reason: "" });
      toast.success("Ledger updated.");
      loadLedger(orgId);
      saas.adminOrgs().then(({ data: d }) => setOrgs(d));
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-credits">
      <h1 className="font-heading text-2xl font-bold text-re-charcoal">Credits</h1>

      <div className="rounded-2xl border border-re-line bg-white p-5">
        <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto]">
          <div className="space-y-1.5">
            <Label>Customer</Label>
            <Select value={orgId} onValueChange={setOrgId}>
              <SelectTrigger className="border-re-line" data-testid="credit-org-select"><SelectValue placeholder="Choose a customer" /></SelectTrigger>
              <SelectContent>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name} ({o.availableCredits})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {balance !== null && <div className="flex items-end"><p className="text-sm text-re-muted">Balance: <span className="font-heading text-2xl font-bold text-re-charcoal" data-testid="credit-balance">{balance}</span></p></div>}
        </div>

        {orgId && (
          <div className="mt-4 grid gap-3 border-t border-re-line pt-4 md:grid-cols-4">
            <div className="space-y-1.5"><Label>Amount (+/-)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="grant-amount" className="border-re-line" /></div>
            <div className="space-y-1.5"><Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}><SelectTrigger className="border-re-line" data-testid="grant-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent></Select>
            </div>
            <div className="space-y-1.5 md:col-span-2"><Label>Reason (required)</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="grant-reason" className="border-re-line" /></div>
            <div className="md:col-span-4"><Button onClick={submit} disabled={busy} data-testid="grant-submit" className="rounded-full bg-re-red text-white hover:bg-re-deepred">{busy ? "Working…" : "Update ledger"}</Button></div>
          </div>
        )}
      </div>

      {orgId && (
        <div className="overflow-x-auto rounded-2xl border border-re-line bg-white" data-testid="ledger-table">
          <Table>
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Amount</TableHead><TableHead>Project</TableHead><TableHead>Reason</TableHead><TableHead>Created by</TableHead></TableRow></TableHeader>
            <TableBody>
              {ledger.map((l) => (
                <TableRow key={l.id} data-testid={`ledger-${l.id}`}>
                  <TableCell className="text-xs text-re-muted">{new Date(l.createdAt).toLocaleString("en-GB")}</TableCell>
                  <TableCell className="text-re-muted">{l.type}</TableCell>
                  <TableCell className={`font-medium ${l.amount >= 0 ? "text-emerald-600" : "text-re-red"}`}>{l.amount > 0 ? `+${l.amount}` : l.amount}</TableCell>
                  <TableCell className="font-mono text-xs text-re-muted">{l.projectId ? l.projectId.slice(0, 8) : "—"}</TableCell>
                  <TableCell className="text-re-muted">{l.reason}</TableCell>
                  <TableCell className="text-xs text-re-muted">{l.createdBy}</TableCell>
                </TableRow>
              ))}
              {ledger.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-re-muted">No entries yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
